// @spec SHL-STR-014
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { NotificationPreferencesForm } from '@/features/notifications/components/NotificationPreferencesForm';
import type { NotificationPreferences } from '@/features/notifications/types';
import { useProfile, useUpdateProfile } from '@/features/profile';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/notifications')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.notifications'),
  }),
  component: NotificationSettingsPage,
});

/**
 * Notification preferences settings page.
 *
 * Allows users to configure their notification preferences including
 * category toggles for in-app and email delivery, quiet hours, and
 * sending test notifications. Uses auto-save with debounce.
 */
function NotificationSettingsPage() {
  const { t } = useTranslation('settings');
  const { data: profile, isLoading, error, refetch } = useProfile();
  const updateProfile = useUpdateProfile();

  const handleUpdatePreferences = (prefs: NotificationPreferences) => {
    updateProfile.mutate({
      preferences: {
        notificationPreferences: prefs,
      },
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('notificationsPage.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('notificationsPage.description')}</p>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-sm text-destructive mb-4">
              {error instanceof Error ? error.message : t('notificationsPage.failedToLoad')}
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t('buttons.tryAgain')}
            </Button>
          </div>
        ) : (
          <NotificationPreferencesForm
            preferences={profile?.preferences.notificationPreferences}
            onUpdate={handleUpdatePreferences}
            isPending={updateProfile.isPending}
            userTimezone={profile?.timezone ?? 'UTC'}
          />
        )}
      </div>
    </div>
  );
}
